/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Content Explorer
 *| (C) Copyright IBM Corp. 2015, 2022
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

/**
 *
 * Override this class to provide localized labels,description for vis properties and slots.
 *
 */

define([
	'underscore',
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'./VisDefinitions',
	'../../DynamicFileLoader',
	'../../widgets/livewidget/util/VisUtil'
], function(_, Class, VisDefinitions, DynamicFileLoader, VisUtil) {

	// List of providers managed by the visDefinitionManager
	const PROVIDERS = [
		'dashboard-analytics/visualizations/definitions/providers/VIPR3Provider',
		'dashboard-analytics/visualizations/definitions/providers/LocalProvider'
	];

	const WILDCARD_KEY = '*';

	const VisDefinitionManager = Class.extend({

		initialAllPromise: null,
		extensionsPromise: null,
		initializeProvidersPromise: null,
		/**
		 * This class is singleton, do not cache dashboard api.
		 * @param params - expects a dashboardApi to access the dashboard API
		 */
		init: function(params) {
			this.dashboard = params.dashboardApi;
			this.logger = params.dashboardApi.getGlassCoreSvc('.Logger');

			if (params.visDefinitions) {
				this.visDefinitions = new VisDefinitions({
					jsonDefinitions: params.visDefinitions
				});
			}

			this.initialAllPromise = null;

			// initialize all visDefinition providers
			this.initializeProviders = this._initProviders(params);
		},

		_getExtensions: function(dashboardApi) {

			if (!this.extensionsPromise) {

				var visualizationExtensionConfig = dashboardApi.getCollectionConfig('visualizationExtension');
				if (visualizationExtensionConfig) {
					var collectionId =  visualizationExtensionConfig.id;
					// load the collection
					this.extensionsPromise = new Promise((resolve, reject) => {
						dashboardApi.findGlassCollection(collectionId)
							.then((extensions) => {
								// convert visualization extensions array to a map (id as key)
								var extentionsMap = _.object(_.map(extensions, function(ext) {
									// ids that are not fully qualified will be prefixed by the feature id
									const prefix = 'com.ibm.bi.dashboard.';
									const idParts = ext.id && ext.id.split(prefix);
									const id = (idParts.length === 1) ? idParts[0] : idParts[1];
									return [id, ext];
								}));
								// @todo temporary code to temporarily enable a visualization only on dev mode
								// edit the extension entry from perspectives/dashboard/contrib/visualizationExtensionCA.json
								let enabledList = [
									'com.ibm.vis.comet'
								];
								if (dashboardApi.isDevInstall()) {
									_.each(enabledList, visId => {
										if (_.isObject(extentionsMap[visId])) {
											extentionsMap[visId].active = true;
										}
									});
								}
								resolve(extentionsMap);
							})
							.catch(reject);
					});
				} else {
					// no extensions available... move on...
					this.extensionsPromise = Promise.resolve({});
				}
			}
			return this.extensionsPromise;
		},

		/**
		 * Load all providers defined on the provider list
		 */
		_initProviders: function(params) {
			if (!this.initializeProvidersPromise) {
				var providers = [];
				this.initializeProvidersPromise = DynamicFileLoader.load(params.providers ? params.providers : PROVIDERS)
					.then((modules) => {
						_.each(modules, (Cls) => {
							providers.push(new(Function.prototype.bind.call(Cls, null, params))());
						});

						return providers;
					});

			}
			return this.initializeProvidersPromise;
		},

		/**
		 * Clear the known vis definitions so they can be refreshed.
		 */
		clearDefinitions: function() {
			this.visDefinitions = null;
			this.initialAllPromise = null;

			this.initializeProviders
				.then((providers) => {
					_.each(providers, (provider) => {
						provider.clear();
					});
				});
		},

		/**
		 * Loads all vis definitions from the server
		 */
		_loadAllDefinitions: function(dashboardApi, optimize) {
			const visDefinitions = this._getVisDefinitions();

			this.initialAllPromise = this.initializeProviders
				.then((providers) => {
					return Promise.all(_.map(providers, (provider) => {
						return provider.getDefinitions(optimize);
					})).then(definitions=>{
						return this._extendDefinitions([].concat.apply([], definitions), dashboardApi);
					}).then((extendDefinitions) => {
						visDefinitions.jsonDefinitions = _.sortBy(extendDefinitions,'label');
						return visDefinitions;
					});
				});

			return this.initialAllPromise;
		},

		/**
		 * Recurrsively deep extend the target definition with the source definition along
		 * with a wildcard source which applies to all objects when a specific source is not defined.
		 * The source should take precedence over the wildcard.
		 * {
				// Example 1: Disable multiMeasure on column chart
				'com.ibm.vis.rave2bundlecolumn': {
					'dataSlots': {
						'values': {
							'multiMeasure': false
						}
					}
				},
				// Example 2: Disable multiMeasure on all visualizations
				'*': {
					'dataSlots': {
						'values': {
							'multiMeasure': false
						}
					}
				},
				// Example 3: Only allow 1 mapping for all slots and mutliMeasure is disabled on all visualizations
				'*': {
					'dataSlots': {
						'values': {
							'multiMeasure': false
						},
						'*': {
							'maxItems': 1
						}
					}
				}
			}
		 * @param {Object} target target defiinition object to override
		 * @param {Object} source source definition object to override with
		 * @param {Object} wildcard wildcard definition object to override with against all objects
		 */
		_extendDefinitionObject: function(target, source, wildcard) {
			let extended = target;

			// Attempt to extend the object only if at least one canidates exists
			if (source || wildcard) {
				const sourceKeys = source ? Object.keys(source) : [];
				const wildcardKeys = wildcard ? Object.keys(wildcard) : [];
				const allKeys = [...Object.keys(target), ...sourceKeys, ...wildcardKeys];

				// Ensure we are not extending the original definition, original definition may also be frozen
				extended =  _.clone(target);

				for (const key of allKeys) {
					// wildcard should not exist in the target
					if (key === WILDCARD_KEY) {
						continue;
					}
					const extKey = (Array.isArray(target) && target[key]) ? target[key].id : key;
					// NOTE. `typeof null` is also 'object'
					if (target[key] && typeof target[key] === 'object') {
						// deep extend with extension and wildcard
						// source takes precedence over the wildcard
						const childSource = (source && source[extKey]) || (wildcard && wildcard[extKey]);
						// The wildcard entry can be obtained as a child of the source or child of the parent wildcard.
						// When obtained from the parent wildcard, it can either be a explicit child (ie. 'dataSlots') or child wildcard ('*').
						// The highest precedence is given to the wildcard child ('*') under the source,
						// and next is the explicit child under the parent wildcard, and the last is the wildcard under the parent wildcard.
						// [{
						// 	id: 'barChart',
						// 	dataSlots: {
						// 		'values': {
						// 			'multiMeasure': true
						// 		}
						// 	}
						// },
						// {
						// 	id: '*',
						// 	dataSlots: {
						// 		'*': {
						// 			'maxItems': 1
						// 		}
						// 	}
						// }]
						const childWildcard = (source && source[WILDCARD_KEY]) || (wildcard && wildcard[extKey]) || (wildcard && wildcard[WILDCARD_KEY]);
						extended[key] = this._extendDefinitionObject(target[key], childSource, childWildcard);
					} else if (key !== 'id' ) {
						// shallow extend all attrubutes giving precedence to the source over the wildecard
						if (source && source.hasOwnProperty(extKey)) {
							extended[key] = source[extKey];
						} else if (wildcard && wildcard.hasOwnProperty(extKey)) {
							extended[key] = wildcard[extKey];
						}
					}
				}
			}
			return extended;
		},

		_extendDefinitions: function(definitions, dashboardApi) {
			// fetch the visualization extensions
			return this._getExtensions(dashboardApi)
				.then((extensions) => {
					// exclude all inactive definitions
					const activeDefs = _.filter(definitions, function(definition) {
						const ext = extensions[definition.id] || { active: true };
						return ext.active !== false;
					});
					const extendedDefs = _.map(activeDefs, definition => {
						try {
							return this._extendDefinitionObject(definition, extensions[definition.id], extensions[WILDCARD_KEY]);
						} catch(e) {
							console.warn(`Failed to extend vis definition >${definition.id}<`, e);
							return definition;
						}
					});
					return extendedDefs;
				})
				.catch(() => {
					return definitions;
				});
		},

		_extendDefinition(definition) {
			return this._extendDefinitions([definition], this.dashboard).then(definitions => {
				return definitions.length ? definitions[0] : definition;
			});
		},

		getBundles: function() {
			return this.getAll().then(all => {
				return all.jsonDefinitions;
			});
		},

		/**
		 *  Load the VisDefinitions list from server.
		 *  Returns a cached copy if this operation is already done.
		 *  @returns a promise
		 */
		getAll: function(dashboard, optimize) {
			if (!dashboard) {
				//The only time there should be no dashboard API is when running unit tests,
				//and in that case, visDefinitions should be provided in the constructor.
				return Promise.resolve(this.visDefinitions);
			}
			if (!this.initialAllPromise) {
				return this._loadAllDefinitions(dashboard, optimize);
			}
			return this.initialAllPromise;
		},

		findProvider: function(id) {
			return this.initializeProviders
				.then((providers) => {
					const promises = _.map(providers, (provider) => {
						// resolve the promise with the provider (if contains) or undefined (if doesn't contain)
						return provider.contains(id)
							.then((contains) => {
								return contains ? provider : undefined;
							})
							.catch(function() {
								return undefined;
							});
					});

					return Promise.all(promises)
						.then((results) => {
							// see if there was promise that resolved with a provider
							var provider = _.find(results, (result) => {
								return !!result;
							});

							if (provider) {
								return provider;
							} else {
								throw new Error('Invalid provider');
							}
						});
				});
		},

		/**
		 * Loads a single vis definition from the server
		 */
		_loadDefinition: function(id) {
			const visDefinitions = this._getVisDefinitions();
			return this.findProvider(id)
				.then((provider) => {
					return provider.getDefinition(id)
						.then((data) => {
							visDefinitions.mapDefinitionsById[id] = data;
							return data;
						});
				})
				.catch(() => {
					throw VisUtil.createVisDefinitionLoadingError(id);
				});
		},

		/**
		 *  Load VisDefinition for specified id
		 *  Updates map in this.visDefinitions on getting the spec
		 *  Returns a cached version if this operation is already done
		 *  @returns a promise
		 */
		get: function(id) {
			var data = this.visDefinitions && this.visDefinitions.mapDefinitionsById[id];
			if (!data) {
				return this._loadDefinition(id);
			}

			return Promise.resolve(data);
		},

		refreshProviders: function(dashboardApi) {
			const visDefinitions = this._getVisDefinitions();
			return this.initializeProviders
				.then((providers = []) => {
					return Promise.all(_.map(providers, (provider) => {
						return provider.refreshProvider(dashboardApi);
					})).then(definitions => {
						return this._extendDefinitions([].concat.apply([], definitions), dashboardApi);
					}).then(extendDefinitions => {
						visDefinitions.jsonDefinitions = _.sortBy(extendDefinitions,'label');
						//Remove cached visDefinitions.mapDefinitionsById that are no longer valid (i.e. got deleted from CM for custom vis)
						visDefinitions.removeObsoleteCachedVisDefinitions();
						return visDefinitions;
					});
				});
		},

		refresh: function(visId) {
			return this.findProvider(visId).then(provider => {
				if (provider) {
					return provider.refresh(visId).then(newDef => {
						//update cache
						if(newDef.renderer) {
							this.visDefinitions.mapDefinitionsById[visId] = newDef;
						}
						return newDef;
					}).then(definition => this._extendDefinition(definition));
				}
			}).catch(() => {
				throw VisUtil.createVisDefinitionLoadingError(visId);
			});
		},

		_getVisDefinitions: function() {
			if (!this.visDefinitions) {
				this.visDefinitions = new VisDefinitions();
			}
			return this.visDefinitions;
		}

	});

	var mgr = null;

	return function(params) {
		if (!mgr || params.providers) {
			mgr = new VisDefinitionManager(params);
		}
		return mgr;
	};
});
